// fetch wrapper — credentials:include + 401 자동 refresh 한 번 시도.
const REFRESH_PATH = '/api/auth/refresh'
const REFRESH_TIMEOUT_MS = 5000
let refreshing: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    // refresh 자체가 hang 되면 isLoading 이 영원히 true 가 되므로 timeout 적용
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)
    refreshing = fetch(REFRESH_PATH, { method: 'POST', credentials: 'include', signal: controller.signal })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => {
        clearTimeout(timer)
        setTimeout(() => { refreshing = null }, 500)
      })
  }
  return refreshing
}

function redirectToLogin(): never {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    const here = window.location.pathname + window.location.search
    window.location.assign('/login?return=' + encodeURIComponent(here))
  }
  throw new Error('unauthorized')
}

export async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(input, { credentials: 'include', ...init })
  if (res.status !== 401 || input.startsWith('/api/auth/')) return res
  const ok = await tryRefresh()
  if (!ok) redirectToLogin()
  const retried = await fetch(input, { credentials: 'include', ...init })
  // refresh 직후에도 401 이면 세션 자체가 무효 — loading 무한 방지를 위해 즉시 /login
  if (retried.status === 401) redirectToLogin()
  return retried
}

async function readJson<T>(method: string, url: string, res: Response): Promise<T> {
  if (!res.ok) {
    let body = ''
    try { body = (await res.text()).slice(0, 200) } catch { /* ignore */ }
    throw new Error(`${method} ${url} ${res.status} ${body}`)
  }
  if (res.status === 204) return undefined as T
  const ct = res.headers.get('content-type') || ''
  if (!ct.toLowerCase().includes('application/json')) {
    const preview = (await res.text()).slice(0, 120).replace(/\s+/g, ' ')
    throw new Error(`${method} ${url} expected JSON, got "${ct}": ${preview}`)
  }
  return res.json() as Promise<T>
}

export async function apiGet<T>(url: string): Promise<T> {
  return readJson<T>('GET', url, await apiFetch(url))
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  return readJson<T>('POST', url, await apiFetch(url, {
    method: 'POST',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }))
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  return readJson<T>('PUT', url, await apiFetch(url, {
    method: 'PUT',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }))
}

export async function apiDelete(url: string): Promise<void> {
  const res = await apiFetch(url, { method: 'DELETE' })
  if (!res.ok) throw new Error(`DELETE ${url} ${res.status}`)
}
